import { useEffect } from 'react';

/**
 * Progressive enhancement for the mobile (≤640px) table card-stack in styles.css. For every data
 * table it (1) copies each column header into a `data-label` on the matching cells — the CSS shows
 * that label before the value, so a stacked card reads "Email: …" with no sideways scroll — and
 * (2) re-asserts the ARIA table roles so screen readers keep full table semantics even though the
 * mobile CSS sets `display: block` (which can otherwise strip the implicit roles).
 *
 * DESKTOP IS UNAFFECTED: the card-stack CSS is gated to `@media (max-width: 640px)`, and these
 * attributes are inert above it (a native table already carries these roles; `data-label` is unused).
 *
 * Runs once on mount and again (rAF-coalesced) on any DOM change, so it survives React re-renders
 * (e.g. react-query list refreshes) with zero per-page markup. The observer watches `childList`
 * only, so writing the attributes never re-triggers it.
 */
export function labelTables(root: ParentNode): void {
  root.querySelectorAll('table').forEach((table) => {
    if (table.getAttribute('role') !== 'table') table.setAttribute('role', 'table');
    const headers = Array.from(table.querySelectorAll(':scope > thead th')).map((th) =>
      (th.textContent ?? '').trim(),
    );
    table
      .querySelectorAll(':scope > thead, :scope > tbody')
      .forEach((g) => g.setAttribute('role', 'rowgroup'));
    table.querySelectorAll(':scope > * > tr').forEach((tr) => tr.setAttribute('role', 'row'));
    table.querySelectorAll(':scope > thead th').forEach((th) => th.setAttribute('role', 'columnheader'));
    table.querySelectorAll(':scope > tbody > tr').forEach((tr) => {
      Array.from(tr.children).forEach((cell, i) => {
        if (!(cell instanceof HTMLElement)) return;
        cell.setAttribute('role', 'cell');
        const label = headers[i];
        if (label && cell.dataset.label !== label) cell.dataset.label = label;
      });
    });
  });
}

/** Mount once (top of the app): keeps every table's cell labels + roles current across re-renders. */
export function useResponsiveTableLabels(): void {
  useEffect(() => {
    labelTables(document);
    let scheduled = false;
    const obs = new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        labelTables(document);
      });
    });
    obs.observe(document.body, { childList: true, subtree: true });
    return () => obs.disconnect();
  }, []);
}
